import AddAlt24 from '@carbon/icons-react/es/add--alt/24';
import Locked16 from '@carbon/icons-react/es/locked/16';
import { Tooltip } from 'antd';
import moment from 'moment';
import React, { FC, useCallback, useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { RateCardLite, RateCardVersion } from 'api/rate-cards';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { humanise } from 'components/atoms/humanised-date';
import { Layout } from 'components/atoms/layout';
import { Tag } from 'components/atoms/tag';
import { ProductiveHeading05 } from 'components/styles';
import { PermissionsContext } from 'contexts/permissions';
import { sortByDates } from 'utils/sort-by-dates';

const StyledSider = styled(Layout.Sider).attrs({
  collapsedWidth: 0,
  collapsible: true,
  defaultCollapsed: false,
  theme: 'light',
  trigger: null,
})`
  .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: var(--spacing-10);
  padding: var(--spacing-05);
  border-right: 1px solid var(--gray-20);
`;

const StyledSiderTitle = styled.div`
  ${ProductiveHeading05};
`;

const StyledAddIcon = styled(AddAlt24)`
  color: var(--blue-60);
`;

const StyledMenu = styled(Menu)`
  flex: 1;
  width: 100%;
`;

const StyledMenuItem = styled(Menu.Item)`
  display: flex;
  justify-content: space-between;

  &:not(.ant-menu-item-only-child) {
    flex-direction: row-reverse;
  }

  &.ant-menu-item:hover,
  &.ant-menu-item-selected {
    color: var(--gray-100);
  }

  &.ant-menu-item-selected {
    background-color: var(--gray-10);
  }
`;

const StyledMenuDivider = styled(Menu.Divider)`
  margin: var(--spacing-07) var(--spacing-04);
`;

const MenuLabelWrapper = styled.div`
  padding: 0 var(--spacing-05);
`;

const VersionName = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--gray-40);

  span {
    color: var(--gray-60);
  }
`;

const DraftVersion = styled.div`
  color: var(--gray-100);
`;

const GrayLockIcon = styled(Locked16)`
  color: var(--gray-50);
`;

enum RateCardStates {
  Active = 'active',
  Upcoming = 'upcoming',
  Past = 'past',
}

interface Props {
  siderWidth: number;
  selectedRateCard: RateCardLite | undefined;
  rateCardVersions: RateCardVersion[];
  selectedRateCardVersion: RateCardVersion;
  isUnsavedRateCardVersionPresent: boolean;
  updateSelectedRateCardVersion: (version: number) => void;
  newRateCardVersionHandler: () => void;
}

export const RateCardVersionsSider: FC<Props> = ({
  siderWidth,
  selectedRateCard,
  rateCardVersions,
  selectedRateCardVersion,
  isUnsavedRateCardVersionPresent,
  updateSelectedRateCardVersion,
  newRateCardVersionHandler,
}) => {
  const { canCreate } = useContext(PermissionsContext);

  const initialVersionSplit = useCallback(
    () => ({
      [RateCardStates.Active]: [] as RateCardVersion[],
      [RateCardStates.Upcoming]: [] as RateCardVersion[],
      [RateCardStates.Past]: [] as RateCardVersion[],
    }),
    [],
  );

  const rateCardVersionSplit = useMemo(
    () =>
      rateCardVersions.reduce((result, version) => {
        const { active, effectiveDate } = version;

        if (active) {
          result.active = result.active
            .concat(version)
            .sort(({ effectiveDate: dateA }, { effectiveDate: dateB }) =>
              sortByDates(dateA, dateB),
            );
        } else if (moment(effectiveDate).isAfter(moment())) {
          result.upcoming = result.upcoming
            .concat(version)
            .sort(({ effectiveDate: dateA }, { effectiveDate: dateB }) =>
              sortByDates(dateA, dateB),
            );
        } else {
          result.past = result.past
            .concat(version)
            .sort(({ effectiveDate: dateA }, { effectiveDate: dateB }) =>
              sortByDates(dateA, dateB),
            );
        }

        return result;
      }, initialVersionSplit()) ?? initialVersionSplit(),
    [initialVersionSplit, rateCardVersions],
  );

  return (
    <StyledSider width={siderWidth} collapsed={selectedRateCard == null}>
      <HeaderWrapper>
        <StyledSiderTitle>
          <FormattedMessage id="settings.rate_cards.sider.title" />
        </StyledSiderTitle>
        {canCreate && (
          <Tooltip
            title={
              isUnsavedRateCardVersionPresent ? null : (
                <FormattedMessage id="settings.rate_cards.list.actions.create" />
              )
            }
          >
            <Button
              type="text"
              icon={<StyledAddIcon />}
              onClick={newRateCardVersionHandler}
              disabled={isUnsavedRateCardVersionPresent}
            />
          </Tooltip>
        )}
      </HeaderWrapper>
      <StyledMenu
        selectedKeys={[selectedRateCardVersion.rateCardVersionId.toString()]}
        mode="inline"
      >
        {Object.entries(rateCardVersionSplit).map(([type, versions], index) => (
          <>
            <MenuLabelWrapper>
              <Tag color="blue">
                <FormattedMessage
                  id={`settings.rate_cards.sider.menu_type.${type}`}
                />
              </Tag>
            </MenuLabelWrapper>
            {versions.length === 0 ? (
              <StyledMenuItem key={`no_${type}_version`} disabled>
                <VersionName>
                  <FormattedMessage id="settings.rate_cards.sider.no_versions" />
                </VersionName>
              </StyledMenuItem>
            ) : (
              versions.map(({ rateCardVersionId, effectiveDate }) => (
                <StyledMenuItem
                  key={rateCardVersionId}
                  onClick={() => {
                    updateSelectedRateCardVersion(rateCardVersionId);
                  }}
                  icon={type !== RateCardStates.Upcoming && <GrayLockIcon />}
                >
                  {rateCardVersionId < 0 ? (
                    <DraftVersion>
                      <FormattedMessage id="settings.rate_cards.sider.version_draft" />
                    </DraftVersion>
                  ) : (
                    <VersionName>
                      <FormattedMessage
                        id="settings.rate_cards.sider.version_name"
                        values={{
                          date: (
                            <>
                              &nbsp;
                              <span>{humanise(moment(effectiveDate))}</span>
                            </>
                          ),
                        }}
                      />
                    </VersionName>
                  )}
                </StyledMenuItem>
              ))
            )}
            {index < Object.keys(rateCardVersionSplit).length - 1 && (
              <StyledMenuDivider />
            )}
          </>
        ))}
      </StyledMenu>
    </StyledSider>
  );
};
